import type { MountedExperiment } from '@learnforge/demo-kit';
import { findDiscontinuities, parse, sampleCurve, type Piece } from '../func';
import { createActionBar } from '../action-bar';
import { createPiecewiseGraph, type PiecewiseGraphHandle } from '../render/piecewise-graph';
import {
  createCartesianViewport,
  createFormulaLegend,
  createSvgEl,
  type CartesianViewportHandle,
  type CartesianViewportView,
} from '@learnforge/demo-render';
import { stringify } from '@learnforge/demo-math/expr';
import { PIECEWISE_PRESETS } from '../data/piecewise-presets';
import type { FuncContext } from '../types';

export function parsePieceBound(source: string): number | null {
  const normalized = source.trim().toLowerCase().replace('−', '-');
  if (normalized === '-∞' || normalized === '-inf' || normalized === '-infinity') {
    return -Infinity;
  }
  if (
    normalized === '∞' ||
    normalized === '+∞' ||
    normalized === 'inf' ||
    normalized === 'infinity'
  ) {
    return Infinity;
  }
  const value = Number(normalized);
  return Number.isFinite(value) ? value : null;
}

function formatPieceBound(value: number): string {
  if (value === -Infinity) return '−∞';
  if (value === Infinity) return '∞';
  return String(value).replace('-', '−');
}

function clonePieces(source: readonly Piece[]): Piece[] {
  return source.map((piece) => ({
    node: piece.node,
    interval: { ...piece.interval },
  }));
}

export function isValidPieceInterval(interval: Piece['interval']): boolean {
  return interval.start !== Infinity && interval.end !== -Infinity && interval.start < interval.end;
}

export function isValidPieceCollection(pieces: readonly Piece[]): boolean {
  if (!pieces.every((piece) => isValidPieceInterval(piece.interval))) return false;
  for (let leftIndex = 0; leftIndex < pieces.length; leftIndex += 1) {
    for (let rightIndex = leftIndex + 1; rightIndex < pieces.length; rightIndex += 1) {
      const left = pieces[leftIndex]!.interval;
      const right = pieces[rightIndex]!.interval;
      const overlapStart = Math.max(left.start, right.start);
      const overlapEnd = Math.min(left.end, right.end);
      if (overlapStart < overlapEnd) return false;
      if (overlapStart !== overlapEnd || !Number.isFinite(overlapStart)) continue;
      const leftContains = intervalContainsPoint(left, overlapStart);
      const rightContains = intervalContainsPoint(right, overlapStart);
      if (leftContains && rightContains) return false;
    }
  }
  return true;
}

function intervalContainsPoint(interval: Piece['interval'], value: number): boolean {
  const afterStart = interval.openLeft ? value > interval.start : value >= interval.start;
  const beforeEnd = interval.openRight ? value < interval.end : value <= interval.end;
  return afterStart && beforeEnd;
}

export function buildEndpointMarkers(
  pieces: readonly Piece[],
): Array<{ x: number; y: number; kind: 'open' | 'closed' }> {
  return pieces
    .flatMap((piece) => {
      const markers: Array<{ x: number; y: number; kind: 'open' | 'closed' }> = [];
      if (Number.isFinite(piece.interval.start)) {
        const y = evaluatePieceAtEndpoint(piece, piece.interval.start);
        if (y !== null) {
          markers.push({
            x: piece.interval.start,
            y,
            kind: piece.interval.openLeft ? 'open' : 'closed',
          });
        }
      }
      if (Number.isFinite(piece.interval.end)) {
        const y = evaluatePieceAtEndpoint(piece, piece.interval.end);
        if (y !== null) {
          markers.push({
            x: piece.interval.end,
            y,
            kind: piece.interval.openRight ? 'open' : 'closed',
          });
        }
      }
      return markers;
    })
    .sort((left, right) => Number(left.kind === 'closed') - Number(right.kind === 'closed'));
}

function evaluatePieceAtEndpoint(piece: Piece, x: number): number | null {
  const sample = sampleCurve(piece.node, x, x, 1)[0] as number;
  return Number.isFinite(sample) ? sample : null;
}

export function buildPieceSamples(
  pieces: readonly Piece[],
  xMin: number,
  xMax: number,
  samplesPerPiece: number,
): { interval: Piece['interval']; sample: Float64Array; className: string; label?: string }[] {
  const visiblePieces = pieces.filter((piece) => {
    if (!isValidPieceInterval(piece.interval)) return false;
    const lo = Math.max(xMin, Number.isFinite(piece.interval.start) ? piece.interval.start : xMin);
    const hi = Math.min(xMax, Number.isFinite(piece.interval.end) ? piece.interval.end : xMax);
    return lo < hi;
  });
  return visiblePieces.map((p, idx) => {
    const lo = Math.max(xMin, Number.isFinite(p.interval.start) ? p.interval.start : xMin);
    const hi = Math.min(xMax, Number.isFinite(p.interval.end) ? p.interval.end : xMax);
    const label = idx === visiblePieces.length - 1 ? 'f(x)' : undefined;
    return {
      interval: {
        start: lo,
        end: hi,
        openLeft: p.interval.openLeft,
        openRight: p.interval.openRight,
      },
      sample: sampleCurve(p.node, lo, hi, samplesPerPiece),
      className: 'fn-curve-primary',
      ...(label !== undefined ? { label } : {}),
    };
  });
}

export function mountPiecewiseBuilder(ctx: FuncContext): MountedExperiment {
  let pieces: Piece[] = clonePieces(PIECEWISE_PRESETS.relu ?? []);

  const svg = createSvgEl('svg', { viewBox: '0 0 800 460', class: 'fn-stage' });
  ctx.root.appendChild(svg);
  const stageG = createSvgEl('g');
  svg.appendChild(stageG);
  const legend = createFormulaLegend(ctx.root);
  let view: CartesianViewportView = { xDomain: [-10, 10], yDomain: [-10, 10], zoom: 1 };
  let viewport: CartesianViewportHandle | null = null;

  function intervalFormula(piece: Piece): string {
    const terms: string[] = [];
    if (Number.isFinite(piece.interval.start))
      terms.push(`x ${piece.interval.openLeft ? '>' : '≥'} ${piece.interval.start}`);
    if (Number.isFinite(piece.interval.end))
      terms.push(`x ${piece.interval.openRight ? '<' : '≤'} ${piece.interval.end}`);
    return terms.join(', ');
  }

  let graphHandle: PiecewiseGraphHandle | null = null;
  function redraw(): void {
    const sampleCount = Math.max(320, Math.round(800 * (globalThis.devicePixelRatio || 1)));
    const visiblePieces = pieces.filter(
      (piece) =>
        isValidPieceInterval(piece.interval) &&
        piece.interval.end >= view.xDomain[0] &&
        piece.interval.start <= view.xDomain[1],
    );
    const samples = buildPieceSamples(visiblePieces, view.xDomain[0], view.xDomain[1], sampleCount);
    const discs = findDiscontinuities(pieces);
    const options = {
      xDomain: view.xDomain,
      yDomain: view.yDomain,
      width: 800,
      height: 460,
      pieces: samples,
      endpointMarkers: buildEndpointMarkers(pieces).filter(
        (marker) => marker.x >= view.xDomain[0] && marker.x <= view.xDomain[1],
      ),
      discontinuityTicks: discs.map((d) => ({ x: d.x, kind: d.kind })),
      i18n: ctx.i18n,
    };
    if (graphHandle) graphHandle.update(options);
    else graphHandle = createPiecewiseGraph(stageG, options);
    legend.setEntries(
      visiblePieces.map((piece, index) => ({
        id: `piece-${index}`,
        formula: `f(x) = ${stringify(piece.node)}, ${intervalFormula(piece)}`,
        color: 'var(--accent)',
      })),
    );
  }

  const presetRow = document.createElement('div');
  presetRow.className = 'fn-panel-row fn-dropdown-row';
  const presetLabel = document.createElement('label');
  presetLabel.textContent = ctx.i18n.t('param.pieces.label');
  const presetSelect = document.createElement('select');
  for (const id of Object.keys(PIECEWISE_PRESETS)) {
    const opt = document.createElement('option');
    opt.value = id;
    opt.textContent = ctx.i18n.t(`preset.${id}.label` as never);
    presetSelect.appendChild(opt);
  }
  const customOption = document.createElement('option');
  customOption.value = 'custom';
  customOption.textContent = ctx.i18n.t('preset.custom.label');
  presetSelect.appendChild(customOption);
  presetSelect.value = 'relu';
  presetSelect.addEventListener('change', () => {
    if (presetSelect.value === 'custom') return;
    pieces = clonePieces(PIECEWISE_PRESETS[presetSelect.value] ?? []);
    renderEditor();
    viewport?.setBase({ xDomain: [-10, 10], yDomain: [-10, 10] });
    redraw();
  });
  presetRow.appendChild(presetLabel);
  presetRow.appendChild(presetSelect);
  ctx.panel.appendChild(presetRow);

  const editor = document.createElement('div');
  editor.className = 'fn-piece-editor';
  ctx.panel.appendChild(editor);

  function markCustom(): void {
    presetSelect.value = 'custom';
  }

  function renderEditor(): void {
    editor.replaceChildren();
    pieces.forEach((piece, index) => {
      const card = document.createElement('section');
      card.className = 'fn-piece-card';

      const header = document.createElement('div');
      header.className = 'fn-piece-card-header';
      const title = document.createElement('strong');
      title.textContent = `${ctx.i18n.t('piece.label')} ${index + 1}`;
      const condition = document.createElement('code');
      condition.textContent = intervalFormula(piece);
      header.append(title, condition);
      card.appendChild(header);

      const expressionLabel = document.createElement('label');
      expressionLabel.textContent = ctx.i18n.t('param.piece-expression.label');
      const expression = document.createElement('input');
      expression.type = 'text';
      expression.value = stringify(piece.node);
      expression.dataset.pieceExpression = String(index);
      expression.addEventListener('change', () => {
        try {
          piece.node = parsePieceExpression(expression.value);
          expression.classList.remove('fn-input-error');
          markCustom();
          redraw();
        } catch {
          expression.classList.add('fn-input-error');
        }
      });
      card.append(expressionLabel, expression);

      const bounds = document.createElement('div');
      bounds.className = 'fn-piece-bounds';
      const boundDefinitions: Array<{
        side: 'start' | 'end';
        label: string;
        openKey: 'openLeft' | 'openRight';
      }> = [
        {
          side: 'start',
          label: ctx.i18n.t('param.piece-start.label'),
          openKey: 'openLeft',
        },
        { side: 'end', label: ctx.i18n.t('param.piece-end.label'), openKey: 'openRight' },
      ];
      for (const definition of boundDefinitions) {
        const group = document.createElement('label');
        group.textContent = definition.label;
        const input = document.createElement('input');
        input.type = 'text';
        input.value = formatPieceBound(piece.interval[definition.side]);
        input.dataset.pieceBound = `${definition.side}-${index}`;
        input.addEventListener('change', () => {
          const value = parsePieceBound(input.value);
          const nextInterval =
            value === null ? null : { ...piece.interval, [definition.side]: value };
          const candidatePieces = nextInterval
            ? pieces.map((candidate, candidateIndex) =>
                candidateIndex === index ? { ...candidate, interval: nextInterval } : candidate,
              )
            : [];
          if (!nextInterval || !isValidPieceCollection(candidatePieces)) {
            input.classList.add('fn-input-error');
            return;
          }
          piece.interval = nextInterval;
          input.classList.remove('fn-input-error');
          markCustom();
          renderEditor();
          redraw();
        });
        const endpoint = document.createElement('select');
        const included = document.createElement('option');
        included.value = 'closed';
        included.textContent = ctx.i18n.t('param.endpoint-included.label');
        const excluded = document.createElement('option');
        excluded.value = 'open';
        excluded.textContent = ctx.i18n.t('param.endpoint-excluded.label');
        endpoint.append(included, excluded);
        endpoint.value = piece.interval[definition.openKey] ? 'open' : 'closed';
        endpoint.addEventListener('change', () => {
          const nextOpen = endpoint.value === 'open';
          const nextInterval = { ...piece.interval, [definition.openKey]: nextOpen };
          const candidatePieces = pieces.map((candidate, candidateIndex) =>
            candidateIndex === index ? { ...candidate, interval: nextInterval } : candidate,
          );
          if (!isValidPieceCollection(candidatePieces)) {
            endpoint.value = piece.interval[definition.openKey] ? 'open' : 'closed';
            endpoint.classList.add('fn-input-error');
            return;
          }
          piece.interval = nextInterval;
          endpoint.classList.remove('fn-input-error');
          markCustom();
          renderEditor();
          redraw();
        });
        group.append(input, endpoint);
        bounds.appendChild(group);
      }
      card.appendChild(bounds);

      if (pieces.length > 1) {
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'fn-remove-piece';
        remove.dataset.removePiece = String(index);
        remove.textContent = ctx.i18n.t('btn.remove-piece');
        remove.addEventListener('click', () => {
          pieces.splice(index, 1);
          markCustom();
          renderEditor();
          redraw();
        });
        card.appendChild(remove);
      }
      editor.appendChild(card);
    });
  }

  function parsePieceExpression(source: string): Piece['node'] {
    return parse(source).node;
  }

  renderEditor();
  redraw();
  viewport = createCartesianViewport(svg, {
    base: { xDomain: [-10, 10], yDomain: [-10, 10] },
    plotRect: { x: 0, y: 0, width: 800, height: 460 },
    onChange(next) {
      view = next;
      redraw();
    },
  });

  createActionBar(
    ctx.actionBar,
    [
      {
        id: 'add-piece',
        labelKey: ctx.i18n.t('btn.add-piece'),
        onClick: () => {
          const last = pieces.at(-1);
          if (!last) return;
          if (!Number.isFinite(last.interval.end)) {
            const boundary = Number.isFinite(last.interval.start) ? last.interval.start + 1 : 1;
            last.interval.end = boundary;
            last.interval.openRight = true;
            pieces.push({
              node: parsePieceExpression('0'),
              interval: {
                start: boundary,
                end: Infinity,
                openLeft: false,
                openRight: true,
              },
            });
          } else {
            pieces.push({
              node: parsePieceExpression('0'),
              interval: {
                start: last.interval.end,
                end: Infinity,
                openLeft: !last.interval.openRight,
                openRight: true,
              },
            });
          }
          markCustom();
          renderEditor();
          redraw();
        },
      },
      {
        id: 'reset',
        labelKey: ctx.i18n.t('btn.reset'),
        onClick: () => {
          pieces = clonePieces(PIECEWISE_PRESETS.relu ?? []);
          presetSelect.value = 'relu';
          renderEditor();
          redraw();
        },
      },
    ],
    { viewReset: { labelKey: ctx.i18n.t('btn.reset-view'), onResetView: () => viewport?.reset() } },
  );
  return {
    dispose() {
      graphHandle?.destroy();
      viewport?.dispose();
      legend.destroy();
      svg.remove();
      ctx.panel.replaceChildren();
      ctx.actionBar.replaceChildren();
    },
  };
}
